/**
 * portalAuthStore — session state for the admin portal.
 *
 * The portal signs in via /admin/auth/login, which returns a single access
 * token (no refresh token) — so the session is deliberately separate from the
 * analytics app's cookie/refresh flow: it lives in sessionStorage, scoped to
 * the tab, and simply ends when the token expires (the API's 401 sends the
 * user back to /portal/login).
 */

import { create } from "zustand";

const STORAGE_KEY = "portal_session";

interface StoredSession {
  token: string;
  /** Epoch ms after which the token is dead. */
  expiresAt: number;
}

function readSession(): StoredSession | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const session: StoredSession = JSON.parse(raw);
    if (!session.token || Date.now() >= session.expiresAt) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

interface PortalAuthStore {
  token: string | null;
  /** Called after a successful login with the API's expires_in (seconds). */
  setSession: (token: string, expiresIn: number) => void;
  clearSession: () => void;
}

export const usePortalAuthStore = create<PortalAuthStore>()((set) => ({
  token: readSession()?.token ?? null,

  setSession: (token, expiresIn) => {
    const session: StoredSession = {
      token,
      expiresAt: Date.now() + expiresIn * 1000,
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    set({ token });
  },

  clearSession: () => {
    sessionStorage.removeItem(STORAGE_KEY);
    set({ token: null });
  },
}));
